export interface Feature {
  name: string;
  description: string;
  entry: boolean;
  core: boolean;
  pro: boolean;
}

export interface FeatureCategory {
  name: string;
  features: Feature[];
}

export const featureCategories: FeatureCategory[] = [
  {
    name: 'Point of Sale',
    features: [
      { name: 'POS Interface', description: 'Full-featured point of sale with product grid, cart, and checkout', entry: true, core: true, pro: true },
      { name: 'Order Types', description: 'Support for dine-in, takeout, delivery, and bar tabs', entry: true, core: true, pro: true },
      { name: 'Split Payments', description: 'Split bills across multiple payment methods', entry: true, core: true, pro: true },
      { name: 'Modifiers & Add-ons', description: 'Customizable product options and modifiers', entry: true, core: true, pro: true },
      { name: 'Receipt Printing', description: 'Thermal receipt printing via ESC/POS printers', entry: true, core: true, pro: true },
      { name: 'PIN-based Login', description: 'Quick staff login using PIN codes', entry: true, core: true, pro: true },
      { name: 'Register Sessions', description: 'Open/close registers with cash reconciliation', entry: true, core: true, pro: true },
      { name: 'Upsell Suggestions', description: 'AI-powered upsell recommendations during checkout', entry: false, core: true, pro: true },
      { name: 'Offline Mode', description: 'Continue selling when internet is down (Desktop app)', entry: false, core: true, pro: true },
    ],
  },
  {
    name: 'Order Management',
    features: [
      { name: 'Order History', description: 'Complete order history with search and filtering', entry: true, core: true, pro: true },
      { name: 'Order Status Tracking', description: 'Track orders through draft, open, kitchen, ready, completed', entry: true, core: true, pro: true },
      { name: 'Refunds & Voids', description: 'Process refunds and void orders with approval workflow', entry: true, core: true, pro: true },
      { name: 'Order Reprinting', description: 'Reprint receipts and kitchen tickets', entry: true, core: true, pro: true },
      { name: 'Delivery Tracking', description: 'Track delivery orders and driver assignments', entry: false, core: true, pro: true },
    ],
  },
  {
    name: 'Kitchen Display System',
    features: [
      { name: 'Kitchen Display (KDS)', description: 'Real-time order queue for kitchen staff', entry: true, core: true, pro: true },
      { name: 'Multiple View Modes', description: 'Grid and list views for order display', entry: true, core: true, pro: true },
      { name: 'Order Type Filtering', description: 'Filter by dine-in, takeout, delivery, bar', entry: true, core: true, pro: true },
      { name: 'Timing Alerts', description: 'SLA monitoring with visual/audio alerts', entry: false, core: true, pro: true },
      { name: 'Kitchen Stations', description: 'Route orders to specific prep stations', entry: false, core: true, pro: true },
    ],
  },
  {
    name: 'Product & Menu Management',
    features: [
      { name: 'Product Catalog', description: 'Create and manage products with images, descriptions, SKUs', entry: true, core: true, pro: true },
      { name: 'Categories', description: 'Organize products into categories', entry: true, core: true, pro: true },
      { name: 'Modifier Groups', description: 'Create customization options for products', entry: true, core: true, pro: true },
      { name: 'Bulk Import', description: 'Import products via spreadsheet', entry: true, core: true, pro: true },
      { name: 'Location-specific Pricing', description: 'Different prices per location', entry: false, core: true, pro: true },
      { name: 'Cost Tracking', description: 'Track product costs and margins', entry: false, core: true, pro: true },
      { name: 'AI Image Generation', description: 'Generate product images with AI', entry: false, core: true, pro: true },
      { name: 'Recipe Standardization', description: 'Standardized recipes with ingredient tracking', entry: false, core: true, pro: true },
    ],
  },
  {
    name: 'Inventory & Stock Control',
    features: [
      { name: 'Inventory Tracking', description: 'Real-time inventory levels and stock management', entry: true, core: true, pro: true },
      { name: 'Low Stock Alerts', description: 'Notifications when stock runs low', entry: true, core: true, pro: true },
      { name: 'Inventory Adjustments', description: 'Adjust stock with reasons and audit trail', entry: true, core: true, pro: true },
      { name: 'Suppliers', description: 'Manage supplier database and contacts', entry: false, core: true, pro: true },
      { name: 'Purchase Orders', description: 'Create and track purchase orders', entry: false, core: true, pro: true },
      { name: 'Receiving', description: 'Process incoming goods from suppliers', entry: false, core: true, pro: true },
      { name: 'Inventory Counts', description: 'Schedule and perform physical stock counts', entry: false, core: true, pro: true },
      { name: 'Inter-location Transfers', description: 'Transfer stock between locations', entry: false, core: true, pro: true },
      { name: 'Raw Materials/Ingredients', description: 'Track component ingredients and supplies', entry: false, core: true, pro: true },
      { name: 'Supplier Invoices', description: 'Track and manage supplier billing', entry: false, core: false, pro: true },
    ],
  },
  {
    name: 'Customer Management',
    features: [
      { name: 'Customer Profiles', description: 'Create and manage customer records', entry: true, core: true, pro: true },
      { name: 'Purchase History', description: 'Track customer purchase history', entry: true, core: true, pro: true },
      { name: 'Customer Import', description: 'Bulk import customer data', entry: true, core: true, pro: true },
      { name: 'Loyalty Tiers', description: 'Bronze, silver, gold customer tiers', entry: false, core: true, pro: true },
      { name: 'Customer Spending Analytics', description: 'Analyze customer spending patterns', entry: false, core: true, pro: true },
      { name: 'Customer Accounts', description: 'Multi-account support per customer', entry: false, core: true, pro: true },
    ],
  },
  {
    name: 'Self-Service Kiosk',
    features: [
      { name: 'QSR Kiosk Interface', description: 'Self-service ordering for quick service', entry: false, core: true, pro: true },
      { name: 'Kiosk Payment Processing', description: 'Accept payments at kiosk', entry: false, core: true, pro: true },
      { name: 'Kiosk Device Management', description: 'Register and manage kiosk devices', entry: false, core: true, pro: true },
      { name: 'Customer Wallet Kiosk', description: 'Customer-facing digital wallet ordering', entry: false, core: false, pro: true },
    ],
  },
  {
    name: 'Reporting & Analytics',
    features: [
      { name: 'Sales Reports', description: 'Daily, weekly, monthly sales analysis', entry: true, core: true, pro: true },
      { name: 'Payment Reports', description: 'Payment method breakdown and analysis', entry: true, core: true, pro: true },
      { name: 'Register Closure Reports', description: 'End-of-shift summaries and reconciliation', entry: true, core: true, pro: true },
      { name: 'Category Reports', description: 'Category-level sales analysis', entry: true, core: true, pro: true },
      { name: 'Retail Dashboard', description: 'Real-time metrics and KPI visualization', entry: false, core: true, pro: true },
      { name: 'Inventory Reports', description: 'Stock levels, valuation, and variance', entry: false, core: true, pro: true },
      { name: 'User Performance Reports', description: 'Staff performance and transaction metrics', entry: false, core: true, pro: true },
      { name: 'SLA Reports', description: 'Service level and fulfillment time tracking', entry: false, core: true, pro: true },
      { name: 'Cashier Variance Reports', description: 'Per-cashier reconciliation and accuracy', entry: false, core: true, pro: true },
      { name: 'Gift Card Reports', description: 'Gift card sales and redemption tracking', entry: false, core: true, pro: true },
      { name: 'Store Credit Reports', description: 'Store credit balance and history', entry: false, core: true, pro: true },
      { name: 'Scheduled Reports', description: 'Automated report generation and delivery', entry: false, core: false, pro: true },
      { name: 'Custom Report Builder', description: 'Build custom reports with filters', entry: false, core: false, pro: true },
    ],
  },
  {
    name: 'Restaurant Features',
    features: [
      { name: 'Table Management', description: 'Visual floor plan and table status', entry: false, core: true, pro: true },
      { name: 'Reservations', description: 'Manage table reservations', entry: false, core: true, pro: true },
      { name: 'Waitlist', description: 'Digital waitlist management', entry: false, core: true, pro: true },
      { name: 'Course Management', description: 'Fire courses in sequence', entry: false, core: true, pro: true },
    ],
  },
  {
    name: 'Team & User Management',
    features: [
      { name: 'Staff Management', description: 'Create and manage staff accounts', entry: true, core: true, pro: true },
      { name: 'Role-based Access', description: 'Pre-defined roles with permissions', entry: true, core: true, pro: true },
      { name: 'Location Access Control', description: 'Control which locations users can access', entry: true, core: true, pro: true },
      { name: 'Custom Permissions', description: 'Fine-grained permission assignment', entry: false, core: true, pro: true },
      { name: 'Approval Workflows', description: 'Manager approval for sensitive actions', entry: false, core: true, pro: true },
      { name: 'Audit Logs', description: 'Complete activity logging and tracking', entry: false, core: true, pro: true },
      { name: 'Custom Roles', description: 'Create custom roles with specific permissions', entry: false, core: false, pro: true },
    ],
  },
  {
    name: 'Security & Authentication',
    features: [
      { name: 'Email/Password Login', description: 'Secure authentication', entry: true, core: true, pro: true },
      { name: 'Session Management', description: 'Active session tracking and logout', entry: true, core: true, pro: true },
      { name: 'Two-Factor Authentication', description: '2FA for enhanced security', entry: false, core: true, pro: true },
      { name: 'Owner 2FA Approval', description: 'Require owner approval for 2FA changes', entry: false, core: false, pro: true },
      { name: 'SSO Integration', description: 'Single sign-on with identity providers', entry: false, core: false, pro: true },
    ],
  },
  {
    name: 'Multi-location & Organization',
    features: [
      { name: 'Single Location', description: 'Operate one business location', entry: true, core: true, pro: true },
      { name: 'Multiple Locations', description: 'Manage multiple business locations', entry: false, core: true, pro: true },
      { name: 'Cross-location Reporting', description: 'Aggregate reports across locations', entry: false, core: true, pro: true },
      { name: 'Centralized Product Management', description: 'Manage products across all locations', entry: false, core: true, pro: true },
      { name: 'Location-specific Settings', description: 'Custom settings per location', entry: false, core: true, pro: true },
    ],
  },
  {
    name: 'Hardware & Integrations',
    features: [
      { name: 'Receipt Printers', description: 'ESC/POS thermal printer support', entry: true, core: true, pro: true },
      { name: 'Cash Drawers', description: 'Automatic cash drawer control', entry: true, core: true, pro: true },
      { name: 'Barcode Scanners', description: 'USB barcode scanner support', entry: true, core: true, pro: true },
      { name: 'Stripe Payments', description: 'Integrated payment processing', entry: true, core: true, pro: true },
      { name: 'API Access', description: 'REST API for custom integrations', entry: false, core: false, pro: true },
      { name: 'Webhooks', description: 'Real-time event notifications', entry: false, core: false, pro: true },
    ],
  },
  {
    name: 'Platform Access',
    features: [
      { name: 'Web Application', description: 'Browser-based POS access', entry: true, core: true, pro: true },
      { name: 'Desktop Application', description: 'Native Windows/Mac/Linux app', entry: false, core: true, pro: true },
      { name: 'Mobile Application', description: 'iOS and Android apps', entry: false, core: true, pro: true },
      { name: 'Offline Capability', description: 'Work without internet connection', entry: false, core: true, pro: true },
    ],
  },
  {
    name: 'Support & Services',
    features: [
      { name: 'Email Support', description: 'Support via email', entry: true, core: true, pro: true },
      { name: 'Knowledge Base', description: 'Self-service documentation', entry: true, core: true, pro: true },
      { name: 'Priority Support', description: 'Faster response times', entry: false, core: true, pro: true },
      { name: 'Phone Support', description: 'Direct phone support line', entry: false, core: false, pro: true },
      { name: 'Dedicated Account Manager', description: 'Personal account representative', entry: false, core: false, pro: true },
      { name: 'Onboarding Assistance', description: 'Guided setup and training', entry: false, core: false, pro: true },
      { name: 'Custom Development', description: 'Custom feature development', entry: false, core: false, pro: true },
    ],
  },
];

export function exportFeaturesToCSV(categories: FeatureCategory[]): string {
  const headers = ['Category', 'Feature', 'Description', 'Entry', 'Core', 'Pro'];
  const rows: string[][] = [headers];

  categories.forEach((category) => {
    category.features.forEach((feature) => {
      rows.push([
        category.name,
        feature.name,
        feature.description,
        feature.entry ? 'Yes' : 'No',
        feature.core ? 'Yes' : 'No',
        feature.pro ? 'Yes' : 'No',
      ]);
    });
  });

  return rows.map((row) => row.map((cell) => `"${cell.replace(/"/g, '""')}"`).join(',')).join('\n');
}

export function getFeatureCounts(): { entry: number; core: number; pro: number; total: number } {
  let entry = 0;
  let core = 0;
  let pro = 0;
  let total = 0;

  featureCategories.forEach((category) => {
    category.features.forEach((feature) => {
      total++;
      if (feature.entry) entry++;
      if (feature.core) core++;
      if (feature.pro) pro++;
    });
  });

  return { entry, core, pro, total };
}
